import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import type { ApiClient } from "./api/client";
import { AuthProvider, type AuthValue, useAuth } from "./auth/AuthContext";
import { isSignedOutError } from "./auth/authErrors";
import { type CurrentUser, CurrentUserProvider } from "./auth/CurrentUser";
import { CALLBACK_PATH, type CognitoAuth } from "./auth/cognitoTokenProvider";
import { AppShell } from "./components/AppShell";
import { CallbackPage } from "./pages/CallbackPage";
import { ContractDetailPage } from "./pages/ContractDetailPage";
import { ContractListPage } from "./pages/ContractListPage";
import { FolderAdminPage } from "./pages/FolderAdminPage";
import { SignInPage } from "./pages/SignInPage";
import { TeamAdminPage } from "./pages/TeamAdminPage";
import { UserAdminPage } from "./pages/UserAdminPage";

/**
 * `auth` is absent in the development token mode, where a token is pasted into
 * an environment variable and there is no session to start or end. Keeping it
 * optional is what lets the SPA run against a deployed API without Cognito.
 */
export function App({ api, auth }: { api: ApiClient; auth?: CognitoAuth }) {
  // Mirrors the stored session so a sign-in, a sign-out or a 401 re-renders.
  const [session, setSession] = useState(() => auth?.currentSession() ?? null);
  const refresh = useCallback(() => {
    setSession(auth?.currentSession() ?? null);
  }, [auth]);

  const value = useMemo<AuthValue>(() => {
    if (auth === undefined) {
      return {
        isSignedIn: true,
        canSignOut: false,
        signIn: () => {},
        signOut: () => {},
        reportSignedOut: () => {},
      };
    }
    return {
      isSignedIn: session !== null,
      ...(session?.email === undefined ? {} : { email: session.email }),
      canSignOut: true,
      signIn: (returnTo) => void auth.signIn(returnTo),
      signOut: () => void auth.signOut(),
      reportSignedOut: () => {
        auth.dropSession();
        setSession(null);
      },
    };
  }, [auth, session]);

  return (
    <BrowserRouter>
      <AuthProvider value={value}>
        <Routes>
          {auth !== undefined && (
            <Route
              path={CALLBACK_PATH}
              element={<CallbackPage auth={auth} onSignedIn={refresh} />}
            />
          )}
          <Route
            path="/"
            element={
              <Protected api={api}>
                <ContractListPage api={api} />
              </Protected>
            }
          />
          <Route
            path="/contracts/:contractId"
            element={
              <Protected api={api}>
                <ContractDetailPage api={api} />
              </Protected>
            }
          />
          <Route
            path="/folders"
            element={
              <Protected api={api}>
                <FolderAdminPage api={api} />
              </Protected>
            }
          />
          <Route
            path="/teams"
            element={
              <Protected api={api}>
                <TeamAdminPage api={api} />
              </Protected>
            }
          />
          <Route
            path="/users"
            element={
              <Protected api={api}>
                <UserAdminPage api={api} />
              </Protected>
            }
          />
          {/* Anything else is a mistyped or stale link, not a 404 page worth
              building: send them to the list. */}
          <Route
            path="*"
            element={
              <Protected api={api}>
                <ContractListPage api={api} />
              </Protected>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Gate every screen on having a session.
 *
 * The signed-out branch keeps the current path so that following a link to a
 * contract, signing in, and landing on that contract is one continuous move.
 */
function Protected({ api, children }: { api: ApiClient; children: ReactNode }) {
  return (
    <AuthGate>
      <RegisterSignIn api={api}>
        <AppShell>{children}</AppShell>
      </RegisterSignIn>
    </AuthGate>
  );
}

/**
 * Tell the API this person has signed in, which is what puts them in the user
 * directory (`GET /me`), and hand the screens what the answer says about them.
 *
 * Blocks nothing. A failure here must not keep anyone out of the contract
 * list — the screens carry on as an ordinary user would see them, and the next
 * visit tries again. A 401 is different: it means the session is gone, and
 * that has to be acted on.
 */
function RegisterSignIn({
  api,
  children,
}: {
  api: ApiClient;
  children: ReactNode;
}) {
  const auth = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    api
      .me()
      .then((me) => setIsAdmin(me.adminSources.length > 0))
      .catch((error: unknown) => {
        if (isSignedOutError(error)) {
          auth.reportSignedOut();
        }
      });
  }, [api, auth]);

  const currentUser = useMemo<CurrentUser>(() => ({ isAdmin }), [isAdmin]);

  return (
    <CurrentUserProvider value={currentUser}>{children}</CurrentUserProvider>
  );
}

function AuthGate({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  if (!isSignedIn) {
    return (
      <SignInPage
        returnTo={`${window.location.pathname}${window.location.search}`}
      />
    );
  }
  return <>{children}</>;
}
